import { useQuery } from '@tanstack/react-query'
import { compact, groupBy, uniqBy } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { twMerge as twm } from 'tailwind-merge'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useScrollPercentage } from '../../hooks/useScrollPercentage'
import { useSearchableClients } from '../../hooks/useSearchableClients'
import { Dashboard } from '../../models/Dashboard'
import { RecordFactory } from '../../models/RecordFactory'
import { Subaccount } from '../../models/Subaccount'
import { User } from '../../models/User'
import { useSubaccountParentUsersByID } from '../../queries/useSubaccountParentUsersByID'
import { e164ToPhone } from '../../utils/phone'
import { PSApplication } from '../app/PSApplication'
import { ClickableTableRow, Table, TableCell } from '../app/design/Table'
import { TableBody, TableHead, TableHeader, TableRow } from '../app/design/Table'
import { ListHeader } from '../common/ListHeader'
import { TableSkeleton } from '../common/skeletons/TableSkeleton'
import { ClientTableCell } from '../dashboards/ClientTableCell'
import { EmptyDashboardLayout } from '../dashboards/EmptyDashboardLayout'

function cityStateZip(user: User | Subaccount) {
    const parts = compact([user.city, user.state, user.postalCode])
    return parts.join(', ')
}

export const ClientsPage = () => {
    const navigate = useNavigate()

    usePageTitle('Clients')

    const { logAndDisplayError } = useErrorHandler()
    const owner = PSApplication.owner()

    const [page, setPage] = useState<{
        hasMore: boolean
        searchKey: string | null
        nextKey: string | null
    }>({
        hasMore: false,
        searchKey: '',
        nextKey: '',
    })

    const [unsearchedUsers, setUnsearchedUsers] = useState<User[]>([])
    const [unsearchedSubaccounts, setUnsearchedSubaccounts] = useState<Subaccount[]>([])

    const [searchText, setSearchText] = useState('')

    const {
        clients: searchedServerUsers,
        subaccounts: searchedServerSubaccounts,
        isLoading: searchIsLoading,
    } = useSearchableClients({
        searchValue: searchText,
        type: 'client+subaccount',
    })

    const { data: fetchedUserData, isLoading } = useQuery({
        queryKey: ['clients', page.searchKey],
        queryFn: async () => {
            if (page.searchKey === null) {
                return null
            }

            const result = await RecordFactory.getInstance().fetchClients(page.searchKey, undefined)
            if (!result.ok) {
                logAndDisplayError(result.error)
                return null
            }
            return result.value
        },
        enabled: page.searchKey !== null,
    })

    useEffect(() => {
        if (!fetchedUserData) {
            return
        }

        setUnsearchedUsers((u) => uniqBy([...u, ...(fetchedUserData.users ?? [])], 'id'))
        setUnsearchedSubaccounts((u) =>
            uniqBy([...u, ...(fetchedUserData.subaccounts ?? [])], 'id')
        )
        setPage((n) => ({
            ...n,
            hasMore: fetchedUserData.users.length === fetchedUserData.limit,
            nextKey:
                fetchedUserData.users.length > 0
                    ? fetchedUserData.users[fetchedUserData.users.length - 1].sortKey
                    : n.searchKey,
        }))
    }, [fetchedUserData])

    const onThresholdReached = useCallback(() => {
        if (page.hasMore && page.nextKey !== null) {
            setPage((p) => ({
                ...p,
                searchKey: p.nextKey,
                nextKey: null,
            }))
        }
    }, [page])

    const { scrollPercentage } = useScrollPercentage(document.getElementById('pocket-suite-main'))

    useEffect(() => {
        if (scrollPercentage >= 65 && !isLoading) {
            onThresholdReached()
        }
    }, [scrollPercentage, isLoading])

    const queriesAreLoading = searchIsLoading || isLoading

    const isSearchActive = searchText !== ''

    const { data: subaccountParentUsersByID } = useSubaccountParentUsersByID(
        searchedServerSubaccounts,
        isSearchActive
    )

    const userList = useMemo(() => {
        const [users, subaccounts] = isSearchActive
            ? [searchedServerUsers, searchedServerSubaccounts]
            : [unsearchedUsers, unsearchedSubaccounts]

        const userIDs = new Set(users.map((user) => user.id))
        const subaccountsByUserID = groupBy(
            subaccounts,
            (subaccount) => subaccount.client?.id ?? ''
        )
        const subaccountsWithoutUsers = subaccounts.filter(
            (subaccount) => !userIDs.has(subaccount.client?.id ?? '')
        )

        return [
            ...users.flatMap((user) => {
                return [user, ...(subaccountsByUserID[user?.id ?? ''] ?? [])]
            }),
            ...(isSearchActive ? subaccountsWithoutUsers : []),
        ]
    }, [
        isSearchActive,
        unsearchedUsers,
        unsearchedSubaccounts,
        searchedServerUsers,
        searchedServerSubaccounts,
        searchIsLoading,
    ])

    return (
        <div className="relative h-screen w-full overflow-y-auto p-8 pt-5">
            <ListHeader
                title="Clients"
                searchText={searchText}
                onSearchTextChange={setSearchText}
                actionButton={
                    owner.allowSubaccount
                        ? {
                              buttonLabel: 'Add',
                              actions: [
                                  {
                                      id: 'add-client',
                                      label: 'Client',
                                      icon: 'user',
                                      onClick: () => navigate('/clients/new'),
                                  },
                                  {
                                      id: 'add-subaccount',
                                      label: Subaccount.labelString(
                                          owner.industry?.subaccountType ?? 'none',
                                          false
                                      ),
                                      icon: Subaccount.iconNameForSubaccountType(
                                          owner.industry?.subaccountType ?? 'none'
                                      ),
                                      onClick: () => navigate('/subaccounts/new'),
                                  },
                              ],
                              intent: 'primary',
                              icon: 'plus.small',
                          }
                        : {
                              label: 'Add',
                              href: '/clients/new',
                              intent: 'primary',
                              icon: 'plus.small',
                          }
                }
            />
            <div className="mt-6 rounded-lg bg-white p-8">
                {userList.length === 0 && queriesAreLoading ? (
                    <TableSkeleton />
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow location="head" className="sticky top-0">
                                <TableHead className="sticky top-0 font-normal">Name</TableHead>
                                <TableHead className="sticky top-0 font-normal">
                                    Contact info
                                </TableHead>
                                <TableHead className="sticky top-0 font-normal">
                                    Street address
                                </TableHead>
                                <TableHead className="sticky top-0 font-normal">
                                    City, State, Zip
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {userList.map((user) => {
                                if (user instanceof Subaccount) {
                                    return (
                                        <ClickableTableRow
                                            key={user.id}
                                            href={`/subaccounts/${user.id}`}
                                        >
                                            <TableCell>
                                                <div className={twm(!isSearchActive && 'pl-13')}>
                                                    <ClientTableCell
                                                        user={
                                                            subaccountParentUsersByID?.[
                                                                user.client?.id ?? ''
                                                            ]
                                                        }
                                                        subaccount={user}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>-</TableCell>
                                            <TableCell>{user.line1 || '-'}</TableCell>
                                            <TableCell>{cityStateZip(user) || '-'}</TableCell>
                                        </ClickableTableRow>
                                    )
                                }
                                else {
                                    return (
                                        <ClickableTableRow
                                            key={user.id}
                                            href={`/clients/${user.id}`}
                                        >
                                            <TableCell>
                                                <ClientTableCell user={user} />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col">
                                                    <span>{user?.email}</span>
                                                    <span>{e164ToPhone(user?.phone)}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>{user.line1 || '-'}</TableCell>
                                            <TableCell>{cityStateZip(user) || '-'}</TableCell>
                                        </ClickableTableRow>
                                    )
                                }
                            })}
                            {userList.length === 0 && !queriesAreLoading && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <EmptyDashboardLayout
                                            dashboard={new Dashboard({ id: 'Clients' })}
                                            searchtext={searchText}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </div>
        </div>
    )
}
